import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { LayoutDashboard, Car, Users, UserCog, Search, Bell, LogOut, Menu, X, ChevronDown, Headphones, Contact, CalendarDays, Handshake, Repeat, Landmark, Wrench, LifeBuoy, ClipboardCheck, ListTodo, FileText, BarChart3, Settings, KeyRound, MessagesSquare, MessageCircle, Megaphone, Plug, Sun, Moon, Banknote, SprayCan, StickyNote } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18n';
import api from '../lib/api';
import { can } from '../lib/permissions';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { NotificationCenter } from '../components/NotificationCenter';
import { AppointmentReminder } from '../components/AppointmentReminder';
import { PostSaleAlert } from '../components/PostSaleAlert';
import { CommandBar } from '../components/CommandBar';
import { WeatherChip } from '../components/WeatherChip';
import { WeatherAlertBanner } from '../components/WeatherAlertBanner';
import { applyAccent } from './Profile';

const Logo = ({ size = 36 }) => (
  <img
    src="/speed-motors-logo.png"
    alt="Speed Motors"
    className="rounded-full object-cover ring-1 ring-foreground/10 shrink-0"
    style={{ width: size, height: size }}
  />
);

const nav = [
  { to: '/app', key: 'dashboard', icon: LayoutDashboard, end: true, perm: 'view_dashboard', group: 'ops' },
  { to: '/app/estoque', key: 'inventory', icon: Car, perm: 'view_inventory', group: 'ops' },
  { to: '/app/recon', key: 'recon', icon: ClipboardCheck, perm: 'view_recon', adminOnly: true, group: 'ops' },
  { to: '/app/trade-in', key: 'tradein', icon: Repeat, perm: 'view_trade_in', adminOnly: true, group: 'ops' },
  { to: '/app/crm', key: 'crm', icon: Users, perm: 'view_crm', group: 'sales' },
  { to: '/app/deals', key: 'deals', icon: Handshake, perm: null, group: 'sales' },
  { to: '/app/finance', key: 'finance', icon: Landmark, perm: 'view_finance', adminOnly: true, group: 'sales' },
  { to: '/app/clientes', key: 'customers', icon: Contact, perm: 'view_dashboard', group: 'sales' },
  { to: '/app/chat', key: 'chat', icon: MessagesSquare, perm: 'view_dashboard', group: 'comms' },
  { to: '/app/bdc', key: 'bdc', icon: Headphones, perm: 'view_bdc', group: 'comms' },
  { to: '/app/agenda', key: 'calendar', icon: CalendarDays, perm: 'view_calendar', group: 'comms' },
  { to: '/app/service', key: 'service', icon: Wrench, perm: 'view_service', group: 'mgmt' },
  { to: '/app/detail', key: 'detail', icon: SprayCan, perm: 'view_dashboard', group: 'mgmt' },
  { to: '/app/post-sale', key: 'postsale', icon: LifeBuoy, perm: 'view_post_sale', group: 'mgmt' },
  { to: '/app/tasks', key: 'tasks', icon: ListTodo, perm: 'view_tasks', group: 'mgmt' },
  { to: '/app/notas', key: 'notes', icon: StickyNote, perm: 'view_dashboard', group: 'mgmt' },
  { to: '/app/financeiro', key: 'financeiro', icon: Banknote, finComm: true, group: 'mgmt' },
  { to: '/app/reports', key: 'reports', icon: BarChart3, perm: 'view_reports', group: 'mgmt' },
  { to: '/app/documents', key: 'documents', icon: FileText, perm: 'view_documents', group: 'mgmt' },
  { to: '/app/equipe', key: 'employees', icon: UserCog, perm: 'view_dashboard', group: 'admin' },
  { to: '/app/landing-pages', key: 'landingpages', icon: Megaphone, perm: 'view_dashboard', simAdminOnly: true, group: 'admin' },
  { to: '/app/integrations', key: 'integrations', icon: Plug, perm: 'view_dashboard', simAdminOnly: true, group: 'admin' },
  { to: '/app/admin', key: 'admin', icon: Settings, perm: 'manage_users', adminOnly: true, group: 'admin' },
];

const NAV_GROUPS = [
  ['ops', 'Operações'], ['sales', 'Vendas & F&I'], ['comms', 'Comunicações'],
  ['mgmt', 'Gestão'], ['admin', 'Administração'],
];

// Role Detail: acesso restrito ao próprio módulo + Estoque, Tarefas, Serviço e Agenda
const DETAIL_NAV = ['detail', 'inventory', 'tasks', 'notes', 'customers', 'service', 'calendar', 'deals'];

const LANGS = [{ c: 'pt', f: '🇧🇷' }, { c: 'en', f: '🇺🇸' }, { c: 'es', f: '🇪🇸' }];

const SUBTITLES = {
  dashboard: 'Acompanhe seus resultados e prossiga suas oportunidades',
  inventory: 'Estoque, custos, aging e margem por unidade',
  crm: 'Pipeline de vendas e follow-up dos leads',
  deals: 'Fluxo de garantia, seguro, titulação, registro e entrega',
  calendar: 'Operações da concessionária',
  bdc: 'Central de atendimento e conversão',
  customers: 'Base de clientes e jornada completa',
  chat: 'Comunicação interna da equipe',
  service: 'Inspeção de entrega e ordens de serviço',
  detail: 'Produtividade do departamento de Detail/Clean',
  postsale: 'Casos de pós-venda e oficina',
  tasks: 'Tickets e tarefas da operação',
  notes: 'Suas notas privadas — só você vê',
  reports: 'Indicadores, profit e produtividade',
  financeiro: 'Garantia estendida, comissões e controle financeiro',
  employees: 'Equipe, cargos e metas mensais',
  integrations: 'Fontes de leads e webhooks',
  landingpages: 'Páginas de captura e submissões',
  admin: 'Usuários, permissões e configurações',
  recon: 'Recondicionamento de veículos',
  tradein: 'Avaliação de trocas',
  finance: 'Financiamento e estrutura de deal',
  documents: 'Documentos da operação',
};

const StatusBar = ({ moduleLabel }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const iv = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(iv); }, []);
  const stamp = `${now.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })} ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  return (
    <footer className="ent-statusbar" data-testid="app-statusbar">
      <span>Speed Motors <span className="mx-2 opacity-40">|</span> Dealer Operating System</span>
      <span className="truncate">{moduleLabel} <span className="mx-2 opacity-40">|</span> v1.0.0 <span className="mx-2 opacity-40">|</span> Hoje, {stamp}</span>
    </footer>
  );
};

const ThemeToggle = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem('sm-theme') || 'dark');
  useEffect(() => {
    document.documentElement.classList.toggle('light', theme === 'light');
    localStorage.setItem('sm-theme', theme);
  }, [theme]);
  return (
    <button data-testid="theme-toggle" title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
      onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
      className="w-9 h-9 rounded-[3px] border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
      style={{ width: 32, height: 32 }}>
      {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
};

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => { applyAccent(user?.prefs?.accent); }, [user?.prefs?.accent]);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try { const { data } = await api.get('/chat/unread-total'); if (alive) setChatUnread(data.total); } catch (e) {}
    };
    poll();
    const iv = setInterval(poll, 12000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  const [cpOpen, setCpOpen] = useState(false);
  const [cpForm, setCpForm] = useState({ atual: '', nova: '', confirma: '' });
  const [cpSaving, setCpSaving] = useState(false);

  const submitChangePassword = async () => {
    if (!cpForm.atual || !cpForm.nova) { toast({ title: 'Preencha todos os campos' }); return; }
    if (cpForm.nova.length < 6) { toast({ title: 'A nova senha deve ter no mínimo 6 caracteres' }); return; }
    if (cpForm.nova !== cpForm.confirma) { toast({ title: 'As senhas não conferem' }); return; }
    setCpSaving(true);
    try {
      await api.post('/auth/change-password', { senha_atual: cpForm.atual, nova_senha: cpForm.nova });
      toast({ title: 'Senha alterada com sucesso' });
      setCpOpen(false); setCpForm({ atual: '', nova: '', confirma: '' });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({ title: 'Erro ao alterar senha', description: typeof d === 'string' ? d : 'Tente novamente.' });
    } finally { setCpSaving(false); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const isActive = (item) =>
    item.end ? location.pathname === item.to : (location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));

  const currentLabel = (() => { const a = nav.find((n) => isActive(n)); return a ? t(a.key) : t('dashboard'); })();
  const currentSub = (() => { const a = nav.find((n) => isActive(n)); return SUBTITLES[a?.key || 'dashboard'] || ''; })();

  const Sidebar = (
    <aside className="w-[236px] shrink-0 h-full bg-sidebar border-r border-border flex flex-col">
      <div className="h-14 flex items-center gap-2.5 px-4 border-b border-border">
        <Logo size={28} />
        <div className="leading-tight">
          <p className="font-display font-bold text-[13px] tracking-[0.14em] text-foreground">SPEED</p>
          <p className="text-[8.5px] tracking-[0.3em] text-muted-foreground">MOTORS</p>
        </div>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto">
        {NAV_GROUPS.map(([gk, glabel]) => {
          const items = nav.filter((item) => item.group === gk).filter((item) => (user?.role === 'Detail' ? DETAIL_NAV.includes(item.key) : true)).filter((item) => (item.finComm ? (['Administrator', 'Owner', 'Salesperson'].includes(user?.role) || (user?.role || '').endsWith('Manager')) : item.finOnly ? (['Administrator', 'Owner'].includes(user?.role) || (user?.role || '').endsWith('Manager')) : item.simAdminOnly ? ['Administrator', 'Owner'].includes(user?.role) : (item.adminOnly ? user?.role === 'Administrator' : (item.perm === 'view_dashboard' || can(user, item.perm)))));
          if (items.length === 0) return null;
          return (
            <div key={gk}>
              <p className="ent-navgroup" data-testid={`nav-group-${gk}`}>{glabel}</p>
              {items.map((item) => {
                const active = isActive(item);
                const Icon = item.icon;
                return (
                  <Link key={item.to} to={item.to} onClick={() => setOpen(false)}
                    data-testid={`nav-${item.key}`}
                    className={`ent-navitem ${active ? 'ent-navitem-active' : ''}`}>
                    <Icon className="w-[15px] h-[15px]" />
                    {t(item.key)}
                    {item.key === 'chat' && chatUnread > 0 && <span data-testid="chat-unread-badge" className="ml-auto num text-[10px] font-bold text-primary">{chatUnread}</span>}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="py-2 border-t border-border">
        <button data-testid="change-password-btn" onClick={() => setCpOpen(true)} className="ent-navitem w-full">
          <KeyRound className="w-[15px] h-[15px]" /> Alterar senha
        </button>
        <button onClick={handleLogout} className="ent-navitem w-full">
          <LogOut className="w-[15px] h-[15px]" /> {t('logout')}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="h-screen w-full flex bg-background text-foreground overflow-hidden">
      {/* desktop sidebar */}
      <div className="hidden lg:flex h-full">{Sidebar}</div>

      {/* mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative z-10 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* topbar */}
        <header className="h-14 shrink-0 border-b border-border bg-card flex items-center justify-between px-4 sm:px-5 sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-foreground/75" onClick={() => setOpen(!open)}>
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-stretch gap-2.5">
              <span className="w-[3px] rounded-[1px] bg-primary" />
              <div className="leading-tight py-0.5 min-w-0">
                <h1 className="font-display font-bold text-[15px] sm:text-[17px] tracking-tight text-foreground truncate max-w-[42vw] sm:max-w-none" data-testid="page-title">{currentLabel}</h1>
                {currentSub && <p className="text-[11px] text-muted-foreground hidden sm:block" data-testid="page-subtitle">{currentSub}</p>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button data-testid="open-command-bar-mobile" onClick={() => setCmdOpen(true)} title="Buscar" className="md:hidden flex items-center justify-center w-9 h-9 rounded-[3px] border border-border text-muted-foreground hover:text-foreground transition-colors">
              <Search className="w-4 h-4" />
            </button>
            <button data-testid="open-command-bar" onClick={() => setCmdOpen(true)} className="hidden md:flex items-center gap-2 border border-border rounded-[3px] px-2.5 h-8 w-56 text-left hover:border-primary/50 transition-colors">
              <Search className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-[12.5px] text-muted-foreground flex-1 truncate">{t('searchPlaceholder')}</span>
              <span className="text-[10px] font-semibold t-label">⌘K</span>
            </button>
            <WeatherChip />
            <div className="hidden sm:flex items-center gap-1.5 text-[11px] font-semibold">
              {LANGS.map((l, i) => (
                <React.Fragment key={l.c}>
                  {i > 0 && <span className="t-label">|</span>}
                  <button data-testid={`lang-${l.c}`} onClick={() => setLang(l.c)} className={`transition-colors ${lang === l.c ? 'text-foreground' : 'text-muted-foreground hover:text-foreground/80'}`}>{l.c.toUpperCase()}</button>
                </React.Fragment>
              ))}
            </div>
            <ThemeToggle />
            <NotificationCenter />
            <div className="flex items-center gap-2 pl-1 border-l border-border">
              <Link to="/app/profile" data-testid="topbar-profile" className="flex items-center gap-2 group">
              <img src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=1a1f29&color=c7ccd4&bold=true`} alt={user?.name} className="w-7 h-7 rounded-full object-cover ml-2 transition-opacity group-hover:opacity-80" referrerPolicy="no-referrer" />
              <div className="hidden sm:block leading-tight">
                <p className="text-[12.5px] font-semibold group-hover:text-primary transition-colors">{user?.name || 'Usuário'}</p>
                <p className="text-[10px] t-label">{user?.role || ''}</p>
              </div>
              </Link>
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground hidden sm:block" />
            </div>
          </div>
        </header>

        <WeatherAlertBanner />

        <main className="relative flex-1 overflow-y-auto px-3 sm:px-5 py-3 sm:py-4 mobile-safe-bottom">
          <div className="relative"><Outlet /></div>
        </main>
        <StatusBar moduleLabel={currentLabel} />

        <CommandBar open={cmdOpen} setOpen={setCmdOpen} />
        <AppointmentReminder />
        <PostSaleAlert />

        <Dialog open={cpOpen} onOpenChange={setCpOpen}>
          <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground">
            <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Alterar minha senha</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Senha atual</label><input data-testid="cp-current" type="password" autoComplete="current-password" className="w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary" value={cpForm.atual} onChange={(e) => setCpForm({ ...cpForm, atual: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Nova senha (mín. 6 caracteres)</label><input data-testid="cp-new" type="password" autoComplete="new-password" className="w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary" value={cpForm.nova} onChange={(e) => setCpForm({ ...cpForm, nova: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Confirmar nova senha</label><input data-testid="cp-confirm" type="password" autoComplete="new-password" className="w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary" value={cpForm.confirma} onChange={(e) => setCpForm({ ...cpForm, confirma: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <button onClick={() => setCpOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
              <button data-testid="cp-save" onClick={submitChangePassword} disabled={cpSaving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{cpSaving ? 'Salvando...' : 'Salvar'}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DashboardLayout;
